import { SlackNotificationType } from "@/lib/shared/enums";
import { postToSlackUrl, sendSlack } from "./slack-sender";

/** A single Slack Block Kit block (header, section, divider, context, …). Loosely typed by design. */
export type SlackBlock = Record<string, unknown>;

/**
 * Sends a rich Slack message — top-level `text` plus a top-level Block Kit `blocks` layout — to the
 * `SLACK_WEBHOOK_URL_<type>` webhook through the shared {@link sendSlack} helper. Posting `blocks`
 * at the top level (rather than nested in an `attachments[]` entry) renders the message as a
 * first-class message instead of an indented secondary attachment, at the cost of the
 * attachment-only colored sidebar. Best-effort and NEVER throws — safe to call from a deferred
 * (`after`) context: a failed POST falls back once to a plain-text message, and a missing webhook
 * URL is a logged no-op.
 *
 * @param type - Which `SLACK_WEBHOOK_URL_<type>` channel to post to.
 * @param text - Top-level message text (put `@mentions` here — they only ping from the top level) and
 *   the notification/fallback preview.
 * @param blocks - The Block Kit blocks to render as the message body.
 * @returns Resolves once the send (or its fallback) has been attempted; never rejects.
 */
export async function sendRichSlackMessageServerOnly(
  type: SlackNotificationType,
  text: string,
  blocks: SlackBlock[]
): Promise<void> {
  const result = await sendSlack({ channel: type, text, blocks });
  if (result.ok || result.reason === "missing_env") return;
  await sendSlack({ channel: type, text });
}

/**
 * Sends a rich Slack message to a webhook URL resolved by the caller (e.g. from a
 * database-configured notification channel) rather than an env-var-backed
 * `SlackNotificationType`. Same best-effort/never-throws contract as
 * {@link sendRichSlackMessageServerOnly}.
 *
 * @param webhookUrl - The destination incoming webhook URL.
 * @param text - Top-level message text and the notification/fallback preview.
 * @param blocks - The Block Kit blocks to render as the message body.
 * @returns Resolves once the send (or its fallback) has been attempted; never rejects.
 */
export async function sendRichSlackMessageToWebhook(
  webhookUrl: string,
  text: string,
  blocks: SlackBlock[]
): Promise<void> {
  const label = "configured webhook";
  const result = await postToSlackUrl(webhookUrl, label, { text, blocks });
  if (result.ok) return;
  await postToSlackUrl(webhookUrl, label, { text });
}
